import { uniqueId } from 'lodash';
// biome-ignore lint/style/useNodejsImportProtocol: node:path causes issues with bundlers
import { basename, extname } from 'path';

import { getClipTypeFromExt, getExt } from '@air/api/shared-utils';
import { ClipType } from '@air/api/types';
import {
  CHUNK_SIZE,
  type FileData,
  isLarge,
  type LargeUpload,
  type SmallUpload,
  type Upload,
  UploadStatus,
} from '@air/redux-uploader';
import { abortUpload as abortUploadRequest } from '@air/upload';

const PERCENTAGE_MULTIPLIER = 100;

export const getPercentage = (amount: number, total: number): number =>
  Math.ceil((amount / total) * PERCENTAGE_MULTIPLIER);

const getFormattedFileData = (file: FileData['file']): FileData['apiUploadData'] => {
  const ext = extname(file.name);
  const name = basename(file.name, ext);

  if (ext[0] !== '.') {
    throw Error('Missing file extension');
  }

  return {
    ext: ext.split('.')[1],
    importedName: name,
    mime: file.type,
    recordedAt: new Date(file.lastModified),
    size: file.size,
    tags: [],
    type: getClipTypeFromExt(getExt(file.name), file.name),
  };
};

export const createUpload = ({ file, batchId }: { file: Upload['file']; batchId: string }): Upload => ({
  apiUploadData: getFormattedFileData(file),
  batchId,
  clipId: '',
  createdAt: Date.now(),
  file,
  id: uniqueId('upload'),
  isNonMedia: getClipTypeFromExt(getExt(file.name), file.name) === ClipType.nonMedia,
  s3Info: {},
  status: UploadStatus.queued,
});

export const abortUpload = (upload: LargeUpload | SmallUpload) => {
  if (isLarge(upload)) {
    abortMultipartUpload(upload.xhr);
  } else if (upload.xhr) {
    abortUploadRequest(upload.xhr);
  }
};

export const abortMultipartUpload = (xhrRequests: LargeUpload['xhr']) => {
  if (xhrRequests) {
    Object.keys(xhrRequests).forEach((partNumber) => {
      const xhr = xhrRequests?.[parseInt(partNumber, 10)];

      if (xhr) {
        abortUploadRequest(xhr);
      }
    });
  }
};

export const getChunksCountForLargeFile = (upload: Upload) =>
  upload.apiUploadData.size ? Math.ceil(upload.apiUploadData.size / CHUNK_SIZE) : 0;

export const getLargeFileChunks = (upload: Upload) => {
  const fileChunks = [];

  if (upload.apiUploadData.size) {
    for (let i = 0; i < getChunksCountForLargeFile(upload); i++) {
      const startPoint = i * CHUNK_SIZE;
      fileChunks.push(upload.file.slice(startPoint, startPoint + CHUNK_SIZE));
    }
  }

  return fileChunks;
};
